import { useState, useEffect } from "react";
import styles from "./route-source-section-modals.module.css";
import { Modal } from "BEDROCK/shared-components/modal";
import { UIIcon } from "PosterEditor/common-components/ui-icon-drawing/ui-icon";
import { StravaConnectButton } from "strava-acquisition/components/strava-connect-button";
import { useStravaActivities } from "strava-acquisition/hooks/use-strava-activities";
import { StravaActivitiesModal } from "PosterEditor/components/strava-activities-modal";
import { useLocation } from "react-router-dom";

interface RouteSourceSectionModalStravaProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal for Strava integration in the desktop editor.
 *
 * Flow:
 * 1. If user doesn't have Strava activities in localStorage, show Connect button
 * 2. Connect button triggers OAuth, returns to editor with activities in localStorage
 * 3. If user has activities, show the activities selection modal
 */
export const RouteSourceSectionModalStrava = (
  props: RouteSourceSectionModalStravaProps
) => {
  const { hasActivitiesData } = useStravaActivities();
  const location = useLocation();
  const [showActivitiesModal, setShowActivitiesModal] = useState(false);

  // Check if we have activities data when modal opens
  useEffect(() => {
    if (props.isOpen && hasActivitiesData()) {
      setShowActivitiesModal(true);
    }
  }, [props.isOpen, hasActivitiesData]);

  const handleClose = () => {
    setShowActivitiesModal(false);
    props.onClose();
  };

  const handleActivityApplied = () => {
    // Keep activities data (7-day expiry) so users can switch between activities
    setShowActivitiesModal(false);
    props.onClose();
  };

  // If we have activities, show the activities selection modal
  if (showActivitiesModal) {
    return (
      <StravaActivitiesModal
        isOpen={props.isOpen}
        onClose={handleClose}
        onActivityApplied={handleActivityApplied}
      />
    );
  }

  // Get the current path including project ID to return after OAuth
  const returnPath = location.pathname;

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose}>
      <div className={`${styles.container} ${styles.stravaConnectModal}`}>
        <div className={styles.titleBar}>
          <UIIcon iconName="STRAVA" size={28} variant="REGULAR" />
          <h1>Connect to Strava</h1>
        </div>
        <div className={styles.content}>
          <p style={{ textAlign: "center", marginBottom: "16px" }}>
            Connect your Strava account to import any of your recent activities
            directly into your poster.
          </p>
          <StravaConnectButton returnTo={returnPath} selectToolbox="lineStyle" />
          <p
            style={{
              fontSize: "12px",
              color: "var(--color-text-secondary)",
              marginTop: "16px",
              textAlign: "center",
            }}
          >
            We only request read access to view your activities.
            <br />
            Your data is never shared or stored.
          </p>
        </div>
      </div>
    </Modal>
  );
};
